import { useState, useEffect } from 'react'
import { authFetch } from '../api/authFetch'
import InlineAlert from '../components/InlineAlert'

export default function Dashboard() {
  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(null)

  useEffect(() => {
    fetchStats()
  }, [])

  const fetchStats = async () => {
    setLoadError(null)
    try {
      const res = await authFetch('/api/stats')
      const data = await res.json()
      if (!res.ok) {
        throw new Error(data.error || res.statusText || 'Failed to load stats')
      }
      setStats(data)
    } catch (err) {
      console.error('Failed to fetch stats:', err)
      setLoadError(err.message || 'Failed to load stats')
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    )
  }

  const StatCard = ({ title, value, subtitle, color = 'indigo' }) => (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <p className="text-sm font-medium text-gray-500">{title}</p>
      <p className={`text-3xl font-bold text-${color}-800 mt-2`}>{value}</p>
      {subtitle && <p className="text-sm text-gray-400 mt-1">{subtitle}</p>}
    </div>
  )

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-8">Dashboard</h1>

      {loadError && (
        <InlineAlert variant="error" onDismiss={() => setLoadError(null)}>
          <p>{loadError}</p>
        </InlineAlert>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard title="Total Users" value={stats?.totalUsers || 0} />
        <StatCard title="Active Users" value={stats?.activeUsers || 0} color="green" />
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Jobs Activity</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <p className="text-2xl font-bold text-indigo-600">{stats?.jobs1Day || 0}</p>
            <p className="text-sm text-gray-500 mt-1">Last 24 hours</p>
          </div>
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <p className="text-2xl font-bold text-indigo-600">{stats?.jobs7Days || 0}</p>
            <p className="text-sm text-gray-500 mt-1">Last 7 days</p>
          </div>
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <p className="text-2xl font-bold text-indigo-600">{stats?.jobs30Days || 0}</p>
            <p className="text-sm text-gray-500 mt-1">Last 30 days</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-8 mb-8">
        <StatCard title="Total Jobs" value={stats?.totalJobs || 0} />
        <StatCard title="Ready Jobs" value={stats?.readyJobs || 0} color="green" />
        <StatCard title="Pending Jobs" value={stats?.pendingJobs || 0} color="yellow" />
        <StatCard title="Other Jobs" value={stats?.otherJobs || 0} color="red" />
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Notifications Activity</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <p className="text-2xl font-bold text-indigo-600">{stats?.notificationsSent1Day || 0}</p>
            <p className="text-sm text-gray-500 mt-1">Last 24 hours</p>
          </div>
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <p className="text-2xl font-bold text-indigo-600">{stats?.notificationsSent7Days || 0}</p>
            <p className="text-sm text-gray-500 mt-1">Last 7 days</p>
          </div>
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <p className="text-2xl font-bold text-indigo-600">{stats?.notificationsSent30Days || 0}</p>
            <p className="text-sm text-gray-500 mt-1">Last 30 days</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-8 mb-8">
        <StatCard title="Waiting Notifications" value={stats?.notificationsWaiting || 0} />
      </div>
    </div>
  )
}
